import { useSchemeDragItem3 } from "@mobx/hooks/useShemeDragItem3";
import mainLocationsStore from "@mobx/store/locations";
import { IBuilding, IRackItemForRack3, isMinerItem3, recalcBuildingOnOff3, toRackItem3 } from "@mobx/store/locations/types";
import { dev } from "@utils/dev";
import { Button, Tooltip } from "antd";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import styles from "./BuildingBtn.module.scss";

export interface IBuildingBtn3Props {
  readonly className?: string;
  readonly active?: boolean;
  readonly building?: IBuilding;

  onClick(): void;
}

const useControl = (props: IBuildingBtn3Props) => {
  const locationStore = mainLocationsStore?.activeLocation;
  const locationId = locationStore?.location.Id;
  const self = useRef<HTMLDivElement | null>(null);
  const miners = useMemo(() => {
    if (!locationStore?.activeBuilding?.racks) return [];
    const r: IRackItemForRack3[] = [];
    for (const rack of locationStore?.activeBuilding?.racks.values()) {
      for (const item of rack.rack_items) {
        if (isMinerItem3(item)) r.push({ ...toRackItem3(item), rackID: rack.id });
      }
    }
    return r;
  }, [locationStore?.activeBuilding?.racks]);
  const buildingData  = useMemo(
    () =>
      recalcBuildingOnOff3({
        ...props.building!,
        miners,
      }),
    [miners]
  );
  const schemeDragItem = useSchemeDragItem3(
    "building",
    `${props.building?.id}`,
    self,
    buildingData
  );
  const [wasSel, setWasSel] = useState(false);
  const schemeToggleTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setWasSel(schemeDragItem.isSelectedForSchemeDrag);
  }, [schemeDragItem.isSelectedForSchemeDrag]);

  useEffect(() => {
    const clearPendingToggle = () => {
      if (schemeToggleTimeout.current) {
        clearTimeout(schemeToggleTimeout.current);
        schemeToggleTimeout.current = null;
      }
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      clearPendingToggle();
      setWasSel(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearPendingToggle();
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const onDelete = async () => {
    if (props.building?.id === undefined) return;
    locationStore?.deleteBuilding(props.building.id);
  };

  const onSelectBuilding = async () => {
    dev.logt("Location", "onSelectBuilding", {
      locationId,
      buildingId: props.building?.id || "",
    });
    props.onClick();
  };

  return {
    isSelectedForSchemeDrag: schemeDragItem.isSelectedForSchemeDrag || wasSel,
    dragID: schemeDragItem.dragID,
    self,
    onDelete,
    onSelectBuilding,
    mouseDown(e: React.MouseEvent<HTMLElement>) {
      if (e.ctrlKey) {
        setWasSel(true);
        if (schemeToggleTimeout.current) {
          clearTimeout(schemeToggleTimeout.current);
        }
        schemeToggleTimeout.current = setTimeout(() => {
          schemeToggleTimeout.current = null;
          schemeDragItem.mouseDown(e);
        }, 100);
        e.stopPropagation();
        e.preventDefault();
      }
    },
  };
};

const BuildingBtn3 = observer((props: IBuildingBtn3Props) => {
  const { className, active, building } = props;
  const { formatMessage } = useIntl();
  const control = useControl(props);
  const addNew = !building;

  return (
    <Tooltip
      open={addNew ? false : undefined}
      trigger={addNew ? undefined : "contextMenu"}
      color='#fff'
      title={
        <div className={styles.tooltip_wrapper}>
          <Button
            className={styles.btn}
            size='small'
            onClick={control.onDelete}
          >
            {formatMessage({ id: "location.__delete" })}
          </Button>
        </div>
      }
    >
      <div
        className={cn(styles.wrapper, className, {
          [styles.active]: active,
          [styles.addNew]: addNew,
          [styles.schemeDrag]: control.isSelectedForSchemeDrag,
        })}
        onClick={(ev) => !(ev.ctrlKey || control.isSelectedForSchemeDrag) && control.onSelectBuilding() }
        ref={control.self}
        onMouseDown={control.mouseDown}
        data-drag-id={control.dragID}
      >
        {addNew
          ? formatMessage({ id: "location.building.add_new" })
          : building.name}
      </div>
    </Tooltip>
  );
});

BuildingBtn3.displayName = "BuildingBtn3";

export default BuildingBtn3;
